import { createCloudRunAxios } from './axios';

// Storefront / Tenant service (promoted from tenant-config). Owns the storefront
// domain: branding, analytics, custom domain, city subscriptions — Spanner-backed.
// Replaces the direct Firestore reads/writes the Client-Admin panels used to do.
const storefront = createCloudRunAxios('tenant-config');

export type ConsentStyle = 'passive' | 'bundled' | 'split';

export interface StorefrontConfig {
  id: string;
  name: string;
  // False when the org row has no storefront/IDX set up (bare defaults). Lets
  // Client-Admin offer "Create IDX" instead of treating every 200 as existing.
  configured?: boolean;
  branding: {
    colors: { primary: string; secondary: string; accent?: string; background?: string; text?: string };
    fonts?: { primary?: string; secondary?: string };
    // rich sections round-tripped via tenant_config (may be absent)
    squeezePage?: unknown;
    footer?: unknown;
    propertyDetails?: unknown;
    teamName?: string;
    brokerageName?: string;
  };
  logo?: string;
  favicon?: string;
  domain: { primary?: string; aliases: string[] };
  skipRentals?: boolean;
  // SPEC-2232: 'passive' | 'bundled' | 'split'. Absent when the org's column is
  // unset, in which case the IDX site falls back to its hardcoded allowlists.
  idxConsentStyle?: ConsentStyle;
  subscribedMLS: Array<{ id: string }>;
  approvedMLS: boolean;
  subscribedCities?: Array<{ id: string; name?: string; stateCode?: string }>;
  analytics?: Record<string, string>;
  orgData?: Record<string, unknown>;
}

export interface StorefrontUpdate {
  name?: string;
  skipRentals?: boolean;
  /** null clears the column, putting the org back on the IDX allowlist fallback. */
  idxConsentStyle?: ConsentStyle | null;
  branding?: Record<string, unknown>;
  analytics?: Record<string, string>;
  domain?: { primary?: string; aliases?: string[] };
}

/** Read full storefront config for a tenant (public GET; same endpoint the IDX site uses). */
export const getStorefrontConfig = async (orgId: string): Promise<StorefrontConfig> => {
  const { data } = await storefront.get<StorefrontConfig>(`/config/${orgId}`);
  return data;
};

/** Partial storefront update (branding/analytics/domain/name). Auth: Firebase token via interceptor. */
export const updateStorefront = async (orgId: string, payload: StorefrontUpdate) => {
  const { data } = await storefront.patch(`/tenant/${orgId}`, payload);
  return data;
};

/** Resolve a hostname to its tenant id (custom-domain ownership check). */
export const resolveHostname = async (
  hostname: string
): Promise<{ hostname: string; tenantId: string; exists: boolean }> => {
  const { data } = await storefront.get('/resolve', { params: { hostname } });
  return data;
};

/** Subscribe/unsubscribe a tenant to a city. */
export const setCitySubscription = async (
  orgId: string,
  cityId: string,
  action: 'subscribe' | 'unsubscribe' = 'subscribe'
) => {
  const { data } = await storefront.post(`/tenant/${orgId}/cities`, { cityId, action });
  return data;
};
